import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { X } from "@phosphor-icons/react";
import api from "@/lib/api";
import { logError } from "@/lib/logger";

/**
 * Multi-value tag/chip input for canonical skill selection.
 *
 * Why not just autocomplete free-text? Because we care about *recall*: a
 * candidate typing "reactjs" and a recruiter searching "React" should
 * match. Backend normalizes on write; this UI helps by driving users to
 * canonical names via autocomplete before submission.
 *
 * Features:
 *   • Autocomplete from GET /api/suggestions/skills (auth) or
 *     /api/public/suggestions/skills (anon apply forms).
 *   • Add chip on Enter, Comma, or Tab.
 *   • Remove chip on X-click or Backspace-on-empty.
 *   • Client-side de-duplication (case-insensitive).
 *   • Fully keyboard-accessible.
 *   • data-testids for every interactive surface.
 *
 * Props:
 *   value          : string[]  (current chips)
 *   onChange       : (next: string[]) => void
 *   placeholder    : string
 *   testId         : string    (prefix; e.g. "profile-skills")
 *   endpoint       : string    (default "/suggestions/skills"; use
 *                                "/public/suggestions/skills" for anon flows)
 *   staticOptions  : string[]  (additional local suggestions, e.g. from JD)
 *   max            : number    (hard cap, defaults 30)
 *   disabled       : bool
 */
export default function SkillsChipInput({
    value = [],
    onChange,
    placeholder = "Type a skill and press Enter…",
    testId = "skills",
    endpoint = "/suggestions/skills",
    staticOptions = [],
    max = 30,
    disabled = false,
}) {
    const [input, setInput] = useState("");
    const [remote, setRemote] = useState([]);
    const [open, setOpen] = useState(false);
    const [highlight, setHighlight] = useState(-1);
    const rootRef = useRef(null);
    const listRef = useRef(null);
    const inputRef = useRef(null);

    // Load the suggestion catalog once. Failures are non-fatal — the user
    // can still type any skill; we just lose autocomplete.
    useEffect(() => {
        let mounted = true;
        api
            .get(endpoint)
            .then((r) => mounted && setRemote(Array.isArray(r.data?.items) ? r.data.items : []))
            .catch((e) => logError("SkillsChipInput remote load failed:", e));
        return () => { mounted = false; };
    }, [endpoint]);

    // Close on outside click
    useEffect(() => {
        const onDoc = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const chipsLower = useMemo(
        () => new Set((value || []).map((v) => v.toLowerCase())),
        [value],
    );

    // De-duped + query-ranked candidate list, excluding chips already on the row
    const options = useMemo(() => {
        const seen = new Set();
        const merged = [];
        [...(staticOptions || []), ...remote].forEach((raw) => {
            const s = (raw || "").toString().trim();
            if (!s) return;
            const key = s.toLowerCase();
            if (seen.has(key) || chipsLower.has(key)) return;
            seen.add(key);
            merged.push(s);
        });

        const q = input.trim().toLowerCase();
        if (!q) return merged.slice(0, 8);

        const startsWith = [];
        const contains = [];
        merged.forEach((opt) => {
            const lo = opt.toLowerCase();
            if (lo === q) return;
            if (lo.startsWith(q)) startsWith.push(opt);
            else if (lo.includes(q)) contains.push(opt);
        });
        return [...startsWith, ...contains].slice(0, 8);
    }, [input, remote, staticOptions, chipsLower]);

    const addChip = useCallback(
        (raw) => {
            const s = (raw || "").trim();
            if (!s) return;
            if (chipsLower.has(s.toLowerCase())) {
                setInput("");
                return;
            }
            if ((value || []).length >= max) return;
            onChange([...(value || []), s]);
            setInput("");
            setHighlight(-1);
            setOpen(true);
        },
        [chipsLower, onChange, value, max],
    );

    const removeAt = useCallback(
        (idx) => {
            const next = [...(value || [])];
            next.splice(idx, 1);
            onChange(next);
            // Keep focus on the input after removing so tab-order feels right.
            requestAnimationFrame(() => inputRef.current?.focus());
        },
        [onChange, value],
    );

    const onKeyDown = (e) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
            setHighlight((h) => Math.min(options.length - 1, (h < 0 ? -1 : h) + 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlight((h) => Math.max(0, h - 1));
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (open && highlight >= 0 && options[highlight]) addChip(options[highlight]);
            else if (input.trim()) addChip(input);
        } else if (e.key === "Tab" && input.trim()) {
            // Don't steal focus — only add if we have a partial match highlighted
            if (open && highlight >= 0 && options[highlight]) {
                e.preventDefault();
                addChip(options[highlight]);
            } else if (input.trim()) {
                e.preventDefault();
                addChip(input);
            }
        } else if (e.key === ",") {
            e.preventDefault();
            if (input.trim()) addChip(input);
        } else if (e.key === "Backspace" && !input && (value || []).length > 0) {
            e.preventDefault();
            removeAt(value.length - 1);
        } else if (e.key === "Escape") {
            setOpen(false);
        }
    };

    // Handle paste: split on commas / newlines so users pasting from a
    // resume ("React, TypeScript, Node.js") get chips immediately.
    const onPaste = (e) => {
        const text = e.clipboardData?.getData("text");
        if (!text || !/[,\n]/.test(text)) return;
        e.preventDefault();
        const parts = text
            .split(/[,\n]/)
            .map((s) => s.trim())
            .filter(Boolean);
        let next = [...(value || [])];
        const cap = max - next.length;
        parts.slice(0, cap).forEach((p) => {
            if (!next.some((v) => v.toLowerCase() === p.toLowerCase())) next.push(p);
        });
        onChange(next);
        setInput("");
    };

    // Scroll highlighted option into view
    useEffect(() => {
        if (!open || highlight < 0 || !listRef.current) return;
        const node = listRef.current.querySelector(`[data-idx="${highlight}"]`);
        if (node) node.scrollIntoView({ block: "nearest" });
    }, [open, highlight]);

    const capReached = (value || []).length >= max;

    return (
        <div ref={rootRef} className="relative">
            <div
                data-testid={`${testId}-chip-input`}
                className={`min-h-[46px] flex flex-wrap items-center gap-1.5 bg-white border border-[#E5E5E5] px-2 py-1.5 focus-within:border-cobalt focus-within:ring-1 focus-within:ring-cobalt transition-colors ${disabled ? "opacity-60" : ""}`}
                onClick={() => inputRef.current?.focus()}
                role="group"
                aria-label="Skills"
            >
                {(value || []).map((v, idx) => (
                    <span
                        key={`${v}-${idx}`}
                        data-testid={`${testId}-chip-${idx}`}
                        className="inline-flex items-center gap-1 bg-cobalt/10 text-ink px-2 py-1 text-xs font-medium"
                    >
                        {v}
                        {!disabled && (
                            <button
                                type="button"
                                data-testid={`${testId}-chip-remove-${idx}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    removeAt(idx);
                                }}
                                className="p-0.5 -mr-0.5 hover:bg-cobalt/25 rounded-sm"
                                aria-label={`Remove ${v}`}
                            >
                                <X size={12} weight="bold" />
                            </button>
                        )}
                    </span>
                ))}
                <input
                    ref={inputRef}
                    data-testid={`${testId}-input`}
                    type="text"
                    value={input}
                    disabled={disabled || capReached}
                    onChange={(e) => {
                        setInput(e.target.value);
                        setOpen(true);
                        setHighlight(-1);
                    }}
                    onFocus={() => setOpen(true)}
                    onKeyDown={onKeyDown}
                    onPaste={onPaste}
                    onBlur={() => {
                        // Commit any pending token on blur so users don't lose
                        // their in-progress typing when tabbing to the next field.
                        if (input.trim()) addChip(input);
                    }}
                    placeholder={
                        capReached
                            ? `Limit ${max} reached`
                            : (value || []).length === 0
                                ? placeholder
                                : ""
                    }
                    className="flex-1 min-w-[140px] bg-transparent text-sm px-1 py-1 focus:outline-none disabled:cursor-not-allowed"
                    autoComplete="off"
                />
            </div>
            {open && !disabled && !capReached && options.length > 0 && (
                <ul
                    ref={listRef}
                    data-testid={`${testId}-listbox`}
                    role="listbox"
                    className="absolute z-30 left-0 right-0 top-full mt-1 max-h-64 overflow-y-auto bg-white border border-[#E5E5E5] shadow-lg"
                >
                    {options.map((opt, idx) => (
                        <li
                            key={opt}
                            data-idx={idx}
                            data-testid={`${testId}-option-${idx}`}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                addChip(opt);
                                inputRef.current?.focus();
                            }}
                            onMouseEnter={() => setHighlight(idx)}
                            className={`px-4 py-2 text-sm cursor-pointer transition-colors ${
                                highlight === idx
                                    ? "bg-cobalt/10 text-ink"
                                    : "text-[#262626] hover:bg-[#FAFAFA]"
                            }`}
                        >
                            {opt}
                        </li>
                    ))}
                </ul>
            )}
            {!disabled && (
                <p className="text-[10px] text-[#A3A3A3] mt-1">
                    Press <kbd className="font-mono bg-[#F5F5F5] px-1 rounded">Enter</kbd> or <kbd className="font-mono bg-[#F5F5F5] px-1 rounded">,</kbd> to add. Backspace to remove. Paste comma-separated lists too.
                    {(value || []).length > 0 && (
                        <span className="ml-2 font-mono">
                            {(value || []).length}/{max}
                        </span>
                    )}
                </p>
            )}
        </div>
    );
}
